import * as React from 'react';
import classNames from 'classnames';
import * as browserUtils from '@wix/thunderbolt-commons/dist/deprecatedBrowserUtils';
import { FileMetaData, IFileUploaderProps } from '../FileUploader.types';
import {
  acceptableFilesTypes,
  allowedKeyCodes,
  facebookAcceptableFileTypes,
  fileNameId,
  testIds,
} from '../constants';
import style from './style/FileUploader.scss';
import { FilesNameList } from './FilesNameList/FilesNameList';
import { NumberOfFilesLink } from './NumberOfFilesLink/NumberOfFilesLink';

const noop = () => {};

const FileUploader: React.ForwardRefRenderFunction<any, IFileUploaderProps> = (
  props,
  ref,
) => {
  const {
    id,
    skin,
    label,
    buttonLabel,
    placeholderLabel,
    value,
    fileType,
    required,
    uploadStatus = 'Not_Started',
    isDisabled,
    shouldShowValidityIndication,
    isInvalidToUpload,
    validationMessage = '',
    withErrorTooltip = false,
    withNumberOfFilesPopper,
    translate,
    validateValueAndShowIndication = noop,
    onChange = noop,
    onSelectedFilesChange = noop,
    onBlur = noop,
    onFocus = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    numFilesLimit = 1,
    externallyOpenPopper,
    externallyClosePopper,
  } = props;

  const fileUploadRef = React.useRef<HTMLInputElement>(null);
  const uploadButtonRef = React.useRef<HTMLInputElement>(null);
  const [acceptString, setAcceptString] = React.useState<string>();

  React.useImperativeHandle(ref, () => ({
    focus() {
      uploadButtonRef.current?.focus();
    },
    blur() {
      uploadButtonRef.current?.blur();
    },
    setCustomValidity(message: string) {
      fileUploadRef.current?.setCustomValidity(message);
    },
    getFiles() {
      return fileUploadRef.current?.files || [];
    },
    resetFiles() {
      if (fileUploadRef.current) {
        fileUploadRef.current.value = '';
      }
    },
  }));

  React.useEffect(() => {
    const acceptableFileExtensionsObject = browserUtils.isFacebookApp(window)
      ? browserUtils.isIOS(window)
        ? facebookAcceptableFileTypes.iOS
        : facebookAcceptableFileTypes.other
      : acceptableFilesTypes;
    setAcceptString(acceptableFileExtensionsObject[fileType]);
  }, [fileType]);

  const isUploading = uploadStatus === 'Started';
  const uploadFailed = uploadStatus === 'Failed';
  const disabled = isUploading || !!isDisabled;
  const withErrorIndication =
    (isInvalidToUpload || uploadFailed) && shouldShowValidityIndication;

  const onUploadButtonClick = () => {
    if (!disabled) {
      fileUploadRef?.current?.click();
      uploadButtonRef?.current?.blur();
    }
  };

  const onUploadButtonKeyDown: React.KeyboardEventHandler = event => {
    if (allowedKeyCodes.includes(event.keyCode)) {
      onUploadButtonClick();
    }
  };

  const onXIconKeyDown: React.KeyboardEventHandler = event => {
    if (allowedKeyCodes.includes(event.keyCode)) {
      onFileRemoval();
    }
  };

  const _onFocus: React.FocusEventHandler = event => {
    if (!disabled) {
      onFocus(event);
    }
  };

  const _onBlur: React.FocusEventHandler = event => {
    if (!disabled) {
      onBlur(event);
    }
  };

  const _onMouseEnter: React.MouseEventHandler<HTMLDivElement> = event => {
    if (!isDisabled) {
      onMouseEnter(event);
    }
  };

  const _onMouseLeave: React.MouseEventHandler<HTMLDivElement> = event => {
    if (!isDisabled) {
      onMouseLeave(event);
    }
  };

  const onFileRemoval = () => {
    if (!isUploading) {
      if (fileUploadRef.current) {
        fileUploadRef.current.value = '';
      }
      onSelectedFilesChange([]);
      // onChange expects to get a full React event, but we rely on viewer platform the add target & context.
      onChange({ type: 'change' } as any);
      validateValueAndShowIndication({
        value: [],
        uploadStatus: 'Not_Started',
      });
      uploadButtonRef.current?.focus();
    }
  };

  const onFileSelectionChange = () => {
    if (fileUploadRef?.current?.files?.length) {
      const selectedFiles: Array<FileMetaData> = Array.from(
        fileUploadRef.current.files,
      ).map(file => ({
        name: file.name,
        size: file.size,
        valid: true,
        validityInfo: {
          invalidKey: '',
          invalidInfo: '',
        },
        fileInfo: null,
        uploadStatus: 'Not_Started',
      }));

      onSelectedFilesChange(selectedFiles);
      // onChange expects to get a full React event, but we rely on viewer platform the add target & context.
      onChange({ type: 'change' } as any);

      validateValueAndShowIndication({
        value: selectedFiles,
        uploadStatus: 'Not_Started',
      });

      uploadButtonRef.current?.focus();
    }
  };

  const rootClassName = classNames(style[skin], {
    [style.hasLabel]: !!label,
    [style.hasButtonLabel]: !!buttonLabel,
    [style.hasPlaceholderLabel]: !!placeholderLabel,
    [style.required]: required,
  });

  const uploadFileButtonClass = classNames(style.uploadFileButton, {
    [style.disabled]: disabled,
    [style.errorIndication]: withErrorIndication,
  });

  return (
    <div
      id={id}
      className={rootClassName}
      onMouseEnter={_onMouseEnter}
      onMouseLeave={_onMouseLeave}
    >
      {!!label && (
        <label className={style.label} htmlFor={`fileInput${id}`}>
          {label}
        </label>
      )}
      <div className={style.inputWithFiles}>
        <input
          type="file"
          required={required}
          accept={acceptString}
          className={style.fileInput}
          onChange={onFileSelectionChange}
          id={`fileInput${id}`}
          ref={fileUploadRef}
          tabIndex={-1}
          multiple={numFilesLimit > 1 ? true : false}
        />
        <div
          data-testid={testIds.uploadButton}
          className={uploadFileButtonClass}
          onClick={onUploadButtonClick}
          onKeyDown={onUploadButtonKeyDown}
          onFocus={_onFocus}
          onBlur={_onBlur}
          ref={uploadButtonRef}
          tabIndex={0}
          role="button"
          aria-describedby={`${id}_${fileNameId}`}
        >
          <svg
            className={style.plusSvg}
            width="15px"
            height="15px"
            viewBox="0 0 15 15"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
              <g className={style.plusIcon}>
                <rect x="6" y="1" width="3" height="13" />
                <rect x="1" y="6" width="13" height="3" />
              </g>
            </g>
          </svg>
          <span className={style.buttonLabel} data-testid={testIds.buttonLabel}>
            {buttonLabel}
          </span>
        </div>
        {withNumberOfFilesPopper ? (
          <NumberOfFilesLink
            id={id}
            value={value}
            placeholderLabel={placeholderLabel}
            translate={translate}
            withErrorIndication={withErrorIndication}
            withErrorTooltip={withErrorTooltip}
            validationMessage={validationMessage}
            disabled={disabled}
            onFileRemoval={onFileRemoval}
            onXIconKeyDown={onXIconKeyDown}
            uploadStatus={uploadStatus}
            externallyOpenPopper={externallyOpenPopper}
            externallyClosePopper={externallyClosePopper}
          />
        ) : (
          <FilesNameList
            id={id}
            skin={skin}
            disabled={disabled}
            value={value}
            placeholderLabel={placeholderLabel}
            withErrorIndication={withErrorIndication}
            withErrorTooltip={withErrorTooltip}
            validationMessage={validationMessage}
            onFileRemoval={onFileRemoval}
            onXIconKeyDown={onXIconKeyDown}
          />
        )}
      </div>
    </div>
  );
};

export default React.forwardRef(FileUploader);
